import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApiData } from "../hooks/useApiData";
import "./PageContent.css";
import BackToTopButton from "../components/BackToTopButton";

const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5001/api";

const SERVER_URL = API_URL.replace(/\/api\/?$/, "");

export default function PageContent() {
  const { mainSlug, subSlug, subSubSlug } = useParams();

  const slugPath = [mainSlug, subSlug, subSubSlug]
    .filter(Boolean)
    .join("/");

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  /* =====================================================
     PROFILE MODAL (leadership details page)
     Content comes from page_id 18 (Dean) / 20 (Med. Supdt.)
     via GET /api/leadership/profile/:slug
     ===================================================== */

  const [profileModal, setProfileModal] = useState({
    open: false,
    loading: false,
    error: false,
    title: "",
    img: null,
    html: "",
  });

  const closeProfile = () =>
    setProfileModal({
      open: false,
      loading: false,
      error: false,
      title: "",
      img: null,
      html: "",
    });

  const openProfile = async () => {
    setProfileModal({
      open: true,
      loading: true,
      error: false,
      title: "",
      img: null,
      html: "",
    });

    try {
      const res = await fetch(`${API_URL}/leadership/profile/${subSlug}`);
      if (!res.ok) throw new Error("Profile not found");
      const data = await res.json();

      setProfileModal({
        open: true,
        loading: false,
        error: false,
        title: data.title || "Profile",
        img: data.img,
        html: data.fullHtml || "",
      });
    } catch (err) {
      console.error("Profile load failed:", err);
      setProfileModal({
        open: true,
        loading: false,
        error: true,
        title: "Profile",
        img: null,
        html: "",
      });
    }
  };

  // Close on Esc + lock background scroll while the modal is open
  useEffect(() => {
    if (!profileModal.open) return;

    const onKey = (e) => {
      if (e.key === "Escape") closeProfile();
    };
    document.addEventListener("keydown", onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [profileModal.open]);

  // Close the modal when navigating to another page
  useEffect(() => {
    closeProfile();
  }, [slugPath]);

  /* =====================================================
     LEADERSHIP DATA (sidebar cards: page_id 17 / 19)
     ===================================================== */

  const { data: leaders } = useApiData("leadership");

  const principal = leaders?.find((leader) => {
    const role = String(leader.role || "").toLowerCase();
    return role.includes("principal") || role.includes("dean");
  });

  const medicalSuperintendent = leaders?.find((leader) => {
    const role = String(leader.role || "").toLowerCase();
    return role.includes("medical superintendent");
  });

  /* =====================================================
     FETCH PAGE CONTENT
     - /leadership-message/:slug  -> /leadership/slug/:slug (page_id 11 / 14)
     - everything else            -> /pagecontent/resolve/...
     ===================================================== */

  const isLeadershipPage =
    mainSlug === "leadership-message" && !!subSlug && !subSubSlug;

  useEffect(() => {
    setLoading(true);

    const endpoint = isLeadershipPage
      ? `${API_URL}/leadership/slug/${subSlug}`
      : `${API_URL}/pagecontent/resolve/${slugPath}`;

    fetch(endpoint)
      .then((res) => {
        if (!res.ok) throw new Error("Page not found");
        return res.json();
      })
      .then((data) => {
        if (isLeadershipPage) {
          // data shape: { id, slug, role, name, img, message, fullHtml }
          setPage({
            page_title: data.role || data.name || "",
            page_contents: data.fullHtml || `<p>${data.message}</p>`,
            attachment: null,
            img: data.img || null,
            name: data.name || "",
          });
        } else {
          setPage(data.data);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setPage(null);
        setLoading(false);
      });
    // eslint-disable-next-line
  }, [slugPath, mainSlug, subSlug, subSubSlug]);

  /* =====================================================
     LOADING
     ===================================================== */

  if (loading) {
    return (
      <div className="content-page-wrapper">
        <div className="content-container">
          <div className="content-loading">
            <div className="content-spinner"></div>
            <p>Loading content...</p>
          </div>
        </div>
      </div>
    );
  }

  /* =====================================================
     PAGE NOT FOUND
     ===================================================== */

  if (!page) {
    return (
      <div className="content-page-wrapper">
        <div className="content-container">
          <div className="content-not-found">
            <div className="not-found-icon">
              <i className="fa fa-file-text-o"></i>
            </div>

            <h2>Page Not Found</h2>

            <p>
              The requested page could not be found or may have been removed.
            </p>

            <Link to="/" className="back-home-btn">
              <i className="fa fa-home"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  /* =====================================================
     BREADCRUMB
     ===================================================== */

  const breadcrumbItems = [mainSlug, subSlug, subSubSlug].filter(Boolean);

  const formatSlug = (slug) => {
    if (!slug) return "";

    return slug
      .replace(/-/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const slugify = (str) =>
    String(str || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  /* =====================================================
     LEADERSHIP MESSAGE PREVIEW (sidebar card)
     ===================================================== */

  const renderOfficialMessage = (leader, title) => {
    if (!leader) {
      return null;
    }

    return (
      <div className="official-message-block">
        {/* GREEN TITLE */}
        <div className="official-message-title">{title}</div>

        {/* MESSAGE AREA */}
        <div className="official-message-body">
          <div className="official-message-top">
            {/* PHOTO */}
            <div className="official-photo-wrapper">
              <img
                src={leader.img}
                alt={leader.name}
                className="official-photo"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    leader.name || "Official"
                  )}&background=0b5d7a&color=fff&size=150`;
                }}
              />
            </div>

            {/* MESSAGE */}
            <div className="official-message-text">
              <p>{leader.message}</p>
            </div>
          </div>

          {/* NAME */}
          <div className="official-name">{leader.name}</div>

          {/* ROLE */}
          <div className="official-role">{leader.role}</div>

          {/* MORE */}
          <div className="official-more-wrapper">
            <Link
              to={`/leadership-message/${leader.slug || slugify(leader.role)}`}
              className="official-more"
            >
              More...
            </Link>
          </div>
        </div>
      </div>
    );
  };

  /* =====================================================
     PAGE
     ===================================================== */

  return (
    <main className="content-page-wrapper">
      {/* =================================================
          BREADCRUMB
          ================================================= */}

      <div className="content-breadcrumb-wrapper">
        <div className="content-container">
          <div className="content-breadcrumb">
            <Link to="/">
              <i className="fa fa-home"></i>
              Home
            </Link>

            {breadcrumbItems.map((item, index) => (
              <React.Fragment key={`${item}-${index}`}>
                <span className="breadcrumb-arrow">
                  <i className="fa fa-angle-right"></i>
                </span>

                <span
                  className={
                    index === breadcrumbItems.length - 1
                      ? "breadcrumb-current"
                      : ""
                  }
                >
                  {formatSlug(item)}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* =================================================
          MAIN LAYOUT
          ================================================= */}

      <div className="content-container">
        <div className="content-layout">
          {/* =================================================
              LEFT SIDEBAR
              ================================================= */}

          <aside className="content-sidebar">
            <div className="official-info-card">
              {/* HEADER */}
              <div className="official-info-header">
                <div className="official-info-icon">
                  <i className="fa fa-envelope-o"></i>
                </div>

                <span>MESSAGE FROM</span>
              </div>

              {/* LEADERS */}
              <div className="official-info-body">
                {renderOfficialMessage(principal, "DEAN AND PRINCIPAL")}

                {renderOfficialMessage(
                  medicalSuperintendent,
                  "MEDICAL SUPERINTENDENT"
                )}
              </div>
            </div>
          </aside>

          {/* =================================================
              RIGHT SIDE - PAGE CONTENT
              ================================================= */}

          <section className="content-main-card">
            {/* PAGE HEADER */}
            <div className="content-header">
              <div
                className={
                  isLeadershipPage
                    ? "notice-page-title leadership-title-row"
                    : "notice-page-title"
                }
                style={{ width: "100%" }}
              >
                <h1>{page.page_title}</h1>

                {/* Profile button — only on the leadership details page */}
                {isLeadershipPage && (
                  <button
                    type="button"
                    className="profile-open-btn"
                    onClick={openProfile}
                  >
                    <i className="fa fa-user"></i> Profile
                  </button>
                )}
              </div>
            </div>

            {/* PAGE BODY */}
            {isLeadershipPage ? (
              <div className="leadership-detail">
                {/* Photo (page_id 11 / 14 image) — left */}
                {page.img && (
                  <aside className="leadership-detail-photo">
                    <div className="leadership-photo-frame">
                      <img
                        src={page.img}
                        alt={page.name || page.page_title}
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            page.name || "Official"
                          )}&background=0b5d7a&color=fff&size=300`;
                        }}
                      />
                    </div>
                  </aside>
                )}

                {/* Content — right */}
                <article
                  className="page-html-content leadership-detail-content"
                  dangerouslySetInnerHTML={{ __html: page.page_contents }}
                />
              </div>
            ) : (
              <article
                className="page-html-content"
                dangerouslySetInnerHTML={{ __html: page.page_contents }}
              />
            )}

            {/* ATTACHMENT */}
            {page.attachment && (
              <div className="content-attachment-section">
                <div className="attachment-heading">
                  <i className="fa fa-paperclip"></i>
                  Related Document
                </div>

                <div className="attachment-card">
                  <div className="attachment-left">
                    <div className="attachment-icon">
                      <i
                        className={
                          /\.pdf$/i.test(page.attachment)
                            ? "fa fa-file-pdf-o"
                            : /\.(doc|docx)$/i.test(page.attachment)
                            ? "fa fa-file-word-o"
                            : /\.(xls|xlsx)$/i.test(page.attachment)
                            ? "fa fa-file-excel-o"
                            : /\.(jpg|jpeg|png|gif)$/i.test(page.attachment)
                            ? "fa fa-file-image-o"
                            : "fa fa-file-o"
                        }
                      ></i>
                    </div>

                    <div className="attachment-info">
                      <strong>Official Document</strong>

                      <span>
                        Click the button to view the attached document.
                      </span>
                    </div>
                  </div>

                  <a
                    href={`${SERVER_URL}/images/menudocuments/${page.attachment
                      .split("/")
                      .pop()}`}
                    target="_blank"
                    rel="noreferrer"
                    className="attachment-view-btn"
                  >
                    <i className="fa fa-eye"></i>
                    View Document
                  </a>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      {/* =================================================
          PROFILE MODAL (page_id 18 / 20 content)
          ================================================= */}

      {profileModal.open && (
        <div className="profile-modal-overlay" onClick={closeProfile}>
          <div
            className="profile-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="profile-modal-header">
              <h3>{profileModal.title || "Profile"}</h3>
              <button
                type="button"
                className="profile-modal-close"
                onClick={closeProfile}
                aria-label="Close"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>

            <div className="profile-modal-body">
              {profileModal.loading ? (
                <div className="content-loading">
                  <div className="content-spinner"></div>
                  <p>Loading profile...</p>
                </div>
              ) : profileModal.error ? (
                <p>Profile could not be loaded.</p>
              ) : (
                <>
                  {profileModal.img && (
                    <div className="profile-modal-photo">
                      <img src={profileModal.img} alt={profileModal.title} />
                    </div>
                  )}
                  <div
                    className="page-html-content"
                    dangerouslySetInnerHTML={{ __html: profileModal.html }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Back to top button */}
      <BackToTopButton />
    </main>
  );
}
